import React, { PureComponent } from "react";
import { Link } from "react-router-dom";
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin
} from "../../../../services/fetchTools";
import { ImageWithFallback } from "../../../../components/imageWithFallback";
import { Plus, Filter, MapPin, Calendar } from "../../../../icons";
import "./gallery.module.css";
import { getCDNUrl } from "../../../../utils/getCDNUrl";

function AlbumSkeletonCard() {
  return (
    <div className="spg-skeleton-card" aria-hidden="true">
      <div className="spg-skeleton-thumb-grid">
        <div className="spg-skeleton spg-skeleton-thumb-large" />
        <div className="spg-skeleton spg-skeleton-thumb" />
        <div className="spg-skeleton spg-skeleton-thumb" />
      </div>
      <div className="spg-skeleton-info">
        <div className="spg-skeleton spg-skeleton-title" />
        <div className="spg-skeleton-meta-row">
          <div className="spg-skeleton spg-skeleton-meta-item" />
          <div className="spg-skeleton spg-skeleton-meta-item" />
        </div>
        <div className="spg-skeleton spg-skeleton-count" />
      </div>
    </div>
  );
}

class EventGallery extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      albums: [],
      album_types: [],
      cities: [],           // ← fetched from eagleeye/get_cities_eagleeye/
      searchQuery: "",
      selectedCity: "all",
      selectedType: "all",
      page: 1,
      total_pages: 1,
      total_count: 0,
      loading: false,
      notFound: false,
    };

    this._searchTimeout = null;

    this.getGalleryDetail   = this.getGalleryDetail.bind(this);
    this.handleScroll       = this.handleScroll.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleCityChange   = this.handleCityChange.bind(this);
    this.handleTypeChange   = this.handleTypeChange.bind(this);
    this.resetAndSearch     = this.resetAndSearch.bind(this);
  }

  componentDidMount() {
    this.fetchCities();
    this.getGalleryDetail();
    window.addEventListener("scroll", this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    if (this._searchTimeout) clearTimeout(this._searchTimeout);
  }

  // ── NEW: fetch city options from the eagle-eye endpoint ──────────────────
  fetchCities() {
    fetch(getLocationOrigin() + "/eagleeye/get_cities_eagleeye/")
      .then(function (response) {
        if (!response.ok) throw new Error("HTTP " + response.status);
        return response.json();
      })
      .then(
        function (data) {
          this.setState({ cities: data.cities || [] });
        }.bind(this)
      )
      .catch(function (err) {
        console.error("fetchCities error:", err);
      });
  }

  buildUrl(page) {
    var params = new URLSearchParams();
    params.set("page", page);

    if (this.state.searchQuery.trim()) {
      params.set("search", this.state.searchQuery.trim());
    }
    // ── FIX: was "city", endpoint expects "location" with a numeric id ──────
    if (this.state.selectedCity && this.state.selectedCity !== "all") {
      params.set("city", this.state.selectedCity);
    }
    if (this.state.selectedType && this.state.selectedType !== "all") {
      params.set("album_type", this.state.selectedType);
    }

    return getLocationOrigin() + "/gallery/get_albums_by_location/?" + params.toString();
  }

  getGalleryDetail() {
    if (this.state.loading) return;

    this.setState({ loading: true, notFound: false });

    var url     = this.buildUrl(this.state.page);
    var options = Object.assign({}, defaultOptions);
    options.method  = "get";
    options.headers = { "content-type": "application/json" };

    fetch(url, options)
      .then(checkStatus)
      .then(parseJSON)
      .then(
        function (data) {
          var newAlbums  = this.state.albums.concat(data.albums);
          var totalPages = data.num_pages;

          this.setState({
            albums:      newAlbums,
            total_pages: totalPages,
            total_count: data.total_count,
            album_types: data.album_types || this.state.album_types,
            loading:     false,
            notFound:    newAlbums.length === 0 && this.state.page >= totalPages,
          });
        }.bind(this)
      )
      .catch(
        function (error) {
          console.log(error);
          this.setState({ loading: false });
        }.bind(this)
      );
  }

  handleScroll() {
    if (
      window.innerHeight + window.scrollY >= document.body.offsetHeight - 400 &&
      !this.state.loading &&
      this.state.page < this.state.total_pages
    ) {
      this.setState({ page: this.state.page + 1 }, this.getGalleryDetail);
    }
  }

  handleSearchChange(e) {
    var value = e.target.value;
    this.setState({ searchQuery: value }, function () {
      if (this._searchTimeout) clearTimeout(this._searchTimeout);
      this._searchTimeout = setTimeout(this.resetAndSearch, 400);
    }.bind(this));
  }

  handleCityChange(e) {
    this.setState({ selectedCity: e.target.value }, this.resetAndSearch);
  }

  handleTypeChange(e) {
    this.setState({ selectedType: e.target.value }, this.resetAndSearch);
  }

  resetAndSearch() {
    this.setState(
      { albums: [], page: 1, total_pages: 1, total_count: 0, notFound: false },
      this.getGalleryDetail
    );
  }

  getThumbnails(album, count) {
    var photos = album.photos && album.photos.length > 0 ? album.photos : [];
    if (photos.length === 0) return [];
    if (photos.length >= count) return photos.slice(0, count);

    var result = [];
    for (var i = 0; i < count; i++) {
      result.push(photos[i % photos.length]);
    }
    return result;
  }

  render() {
    var self          = this;
    var albums        = this.state.albums;
    var skeletonCount = albums.length === 0 ? 6 : 3;

    return (
      <div className="spg-container">

        {/* HEADER */}
        <div className="spg-header">
          <div>
            <h1 className="spg-title">Gallery</h1>
            <p className="spg-subtitle">Browse and manage event photo albums</p>
          </div>
          <Link to="/crud/gallery/add/details">
            <button className="spg-add-btn">
              <Plus width="15" height="15" />
              Add Album
            </button>
          </Link>
        </div>

        {/* FILTERS */}
        <div className="spg-filters-box">
          <div className="spg-filters-title">
            <Filter width="24" height="24" />
            <h3>Filters</h3>
          </div>
          <div className="spg-filters-grid">
            <input
              type="text"
              placeholder="Search albums..."
              value={this.state.searchQuery}
              onChange={this.handleSearchChange}
              className="spg-input"
            />

            {/* ── FIX: dynamically populated from API, value = city.id (number) ── */}
            <select
              value={this.state.selectedCity}
              onChange={this.handleCityChange}
              className="spg-select"
            >
              <option value="all">All Cities</option>
              {this.state.cities.map(function (city) {
                return (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                );
              })}
            </select>

            <select
              value={this.state.selectedType}
              onChange={this.handleTypeChange}
              className="spg-select"
            >
              <option value="all">All Types</option>
              {this.state.album_types.map(function (type) {
                return (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* NOT FOUND */}
        {this.state.notFound && !this.state.loading && (
          <div className="spg-not-found">
            <p className="spg-not-found-title">No albums found</p>
            <p className="spg-not-found-sub">
              No albums matched your search. Try adjusting your filters.
            </p>
          </div>
        )}

        {/* GRID */}
        <div className="spg-albums-grid">
          {albums.map(function (album) {
            var albumName =
              album.name && album.name !== "undefined"
                ? album.name
                : album.tournament && album.tournament.name
                ? album.tournament.name
                : "Untitled Album";

            var thumbnails = self.getThumbnails(album, 3);

            return (
              <Link
                key={album.id}
                to={"/crud/gallery/" + album.id}
                className="spg-album-card"
              >
                <div className="spg-thumbnail-grid">
                  {thumbnails.map(function (photo, idx) {
                    return (
                      <div
                        key={photo.id + "-" + idx}
                        className={
                          idx === 0 ? "spg-thumb spg-thumb-large" : "spg-thumb"
                        }
                      >
                        <ImageWithFallback
                          src={getCDNUrl(photo.image, "small")}
                          alt={albumName}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="spg-album-info">
                  <h3 className="spg-album-title">{albumName}</h3>
                  <div>
                    <div className="spg-meta">
                      {(album.tournament && album.tournament.location) || album.location ? (
                        <span className="spg-meta-item">
                          <MapPin width="15" height="15" />
                          {album.tournament && album.tournament.location || album.location}
                        </span>
                      ) : null}
                      {album.tournament && album.tournament.date && (
                        <span className="spg-meta-item">
                          <Calendar width="15" height="15" />
                          {album.tournament.date}
                        </span>
                      )}
                      {album.date_of_organized && !album.tournament && (
                        <span className="spg-meta-item">
                          <Calendar width="15" height="15" />
                          {album.date_of_organized}
                        </span>
                      )}
                    </div>
                    <div className="spg-photo-count">
                      {album.photos ? album.photos.length : 0} Photos
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          {this.state.loading &&
            Array.from({ length: skeletonCount }).map(function (_, i) {
              return <AlbumSkeletonCard key={"skeleton-" + i} />;
            })}
        </div>

      </div>
    );
  }
}

export default EventGallery;


// WEBPACK FOOTER //
// ./src/app/views/partner/crud/gallery/index.js